import { BookingPunchPassState } from "../../../../atoms/types";
import type { useIsViewMoreVisible_Booking$data } from "./__generated__/useIsViewMoreVisible_Booking.graphql";

type OrderEdges = NonNullable<
  NonNullable<useIsViewMoreVisible_Booking$data["orders"]>["edges"]
>;
type OrderEdge = NonNullable<OrderEdges[number]>;
type OrderNode = NonNullable<OrderEdge["node"]>;
type LineItemGroup = NonNullable<
  NonNullable<OrderNode["lineItemGroups"]>[number]
>;
type LineItem = NonNullable<NonNullable<LineItemGroup["lineItems"]>[number]>;

export type GetIsViewMoreVisibleParams = {
  hidePriceDetails: boolean;
  isDurationBasedFacility: boolean;
  anyBookingFees: boolean;
  hasRoundLineItems: boolean;
  hasBookingChargeGroups: boolean;
  hasRoundPunchPasses: boolean;
  hasBookingPunchPasses: boolean;
};

const isValidLineItem = (lineItem: LineItem | null | undefined): boolean =>
  Boolean(
    lineItem &&
      lineItem.id &&
      lineItem.__typename &&
      lineItem.name &&
      lineItem.price != null &&
      lineItem.type
  );

export const hasValidLineItemsForRounds = (
  orderEdges: OrderEdges | null | undefined,
  roundIds: ReadonlyArray<string>
): boolean => {
  const roundIdSet = new Set(roundIds);

  return (
    orderEdges?.some((edge) =>
      edge?.node?.lineItemGroups?.some((group) => {
        const roundId = group?.round?.id;
        if (!roundId || !roundIdSet.has(roundId)) {
          return false;
        }

        return group.lineItems?.some(isValidLineItem) ?? false;
      })
    ) ?? false
  );
};

export const hasPunchPassesForTarget = (
  punchPassState: BookingPunchPassState | null,
  targetId: string | null | undefined
): boolean => {
  if (!targetId) {
    return false;
  }

  const entry = punchPassState?.byTargetId?.[targetId];
  if (!entry) {
    return false;
  }

  return (
    Boolean(entry.appliedRatePunchPass) ||
    Boolean(entry.appliedTransportationPunchPass) ||
    entry.availableRatePunchPasses.length > 0 ||
    entry.availableTransportationPunchPasses.length > 0 ||
    entry.invalidPunchPasses.length > 0
  );
};

export const getIsViewMoreVisible = ({
  hidePriceDetails,
  isDurationBasedFacility,
  anyBookingFees,
  hasRoundLineItems,
  hasBookingChargeGroups,
  hasRoundPunchPasses,
  hasBookingPunchPasses,
}: GetIsViewMoreVisibleParams): boolean => {
  const hasExpandableRoundDetails =
    !hidePriceDetails &&
    (hasRoundLineItems || anyBookingFees || hasRoundPunchPasses);

  const hasExpandableBookingDetails =
    isDurationBasedFacility &&
    ((!hidePriceDetails && anyBookingFees) ||
      (!hidePriceDetails && hasBookingChargeGroups) ||
      hasBookingPunchPasses);

  return hasExpandableRoundDetails || hasExpandableBookingDetails;
};
